<template>
    <MPModal
        v-model="open"
        variant="side"
        size="xl"
        :title="`Conflict - ${resourceName}`"
    >
        <div class="text-muval-gray-1 flex flex-col gap-6">
            <div
                v-for="reason in reasons"
                :key="reason.type"
                class="flex flex-col gap-3"
            >
                <MuvalBanner
                    inline
                    data-conflict-reason
                >
                    {{ reason.message }}
                </MuvalBanner>

                <div
                    v-for="(detail, index) in reason.details ?? []"
                    :key="`${reason.type}-${detail.id ?? index}`"
                    class="rounded-muval-2 border-muval-gray-5 flex flex-col gap-1 border px-4 py-3"
                    data-conflict-card
                >
                    <div class="flex flex-wrap items-center gap-2">
                        <!--
                            A diary entry wears its own type badge. Every diary event
                            is `MISCELLANEOUS`, so the event-type lozenge would tell
                            an operator nothing about whether the resource is on
                            leave, in for maintenance or out on a supply run.
                        -->
                        <Lozenge
                            v-if="detail.entry_type"
                            class="rounded-full!"
                            :style="{
                                backgroundColor: `${detail.entry_type.colour || FALLBACK_COLOUR}1A`,
                                color: detail.entry_type.colour || FALLBACK_COLOUR,
                            }"
                        >
                            <SvgIcon
                                :name="detail.entry_type.icon || FALLBACK_ICON"
                                class="mr-1 size-3 shrink-0"
                            />
                            {{ detail.entry_type.name }}
                        </Lozenge>

                        <Lozenge
                            v-else-if="detail.type"
                            class="rounded-full!"
                            :class="actionStyle(detail.type).class"
                        >
                            <SvgIcon
                                :name="actionStyle(detail.type).icon"
                                class="mr-1 size-3 shrink-0"
                                :class="actionStyle(detail.type).iconClass || ''"
                            />
                            {{ detail.action_lozenge_label || detail.type }}
                        </Lozenge>

                        <span
                            v-if="detail.order_ref || detail.allocated_capacity"
                            class="text-muval-gray-2 text-xs"
                        >
                            {{
                                [detail.order_ref, detail.allocated_capacity ? `${detail.allocated_capacity}m³` : null]
                                    .filter(Boolean)
                                    .join(' · ')
                            }}
                        </span>
                    </div>

                    <span
                        class="text-muval-gray-1 text-sm font-medium"
                        v-text="detail.heading || detail.label || 'Scheduled work'"
                    ></span>

                    <span
                        v-if="locationLine(detail)"
                        class="text-muval-gray-2 text-sm"
                        v-text="locationLine(detail)"
                    ></span>

                    <!--
                        Date and time are separated the way the operations diary
                        separates them, and the timezone code is printed verbatim
                        beside a verbatim wall clock: these windows are naive-local,
                        so the label names the times rather than converting them.
                    -->
                    <span
                        v-if="detail.start_datetime"
                        class="text-muval-gray-2 text-sm"
                    >
                        {{ dayjs(detail.start_datetime).format('DD/MM/YY') }}
                        <span class="text-muval-gray-4 px-1">|</span>
                        {{ timeRange(detail) }}
                        <template v-if="detail.timezone_code">({{ detail.timezone_code }})</template>
                    </span>
                </div>
            </div>
        </div>
    </MPModal>
</template>

<script setup lang="ts">
import type { CompanyDiaryAvailabilityDetail, CompanyDiaryAvailabilityReason } from '@/api/companyDiary';
import MPModal from '@components/modal/MPModal.vue';
import MuvalBanner from '@components/MuvalBanner.vue';
import Lozenge from '@components/Lozenge.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import { getActionStyle } from '@/utilities/actionIcons';
import { COMPANY_DIARY_FALLBACK_COLOUR, COMPANY_DIARY_FALLBACK_ICON } from './companyDiaryTypeOptions';
import useDayJs from '@plugins/useDayJs';

/**
 * Every clash one resource has with the entry's window, as a side panel.
 *
 * It replaces an inline "Why?" accordion on the resource row. The reasons behind a
 * conflict are a list of *other jobs and entries*, each needing a type, a customer,
 * a place and a window to be recognisable - a row-height disclosure could show a
 * date, which is not enough to go and fix anything.
 *
 * Read-only by design. The operations conflict panel offers "Remove from Run"
 * buttons; the diary deliberately does not, because the answer here is nearly
 * always to book the resource off anyway and re-crew the job elsewhere.
 */
defineProps<{
    resourceName: string;
    /** Already filtered to the non-available reasons by the caller. */
    reasons: CompanyDiaryAvailabilityReason[];
}>();

const open = defineModel<boolean>({ required: true });

const dayjs = useDayJs();

const FALLBACK_COLOUR = COMPANY_DIARY_FALLBACK_COLOUR;
const FALLBACK_ICON = COMPANY_DIARY_FALLBACK_ICON;

const actionStyle = (type: string) => getActionStyle(type);

/** A transit names both ends; everything else names the one place it happens. */
const locationLine = (detail: CompanyDiaryAvailabilityDetail): string | null => {
    if (!detail.location) return null;

    return detail.end_location ? `${detail.location} → ${detail.end_location}` : detail.location;
};

const timeRange = (detail: CompanyDiaryAvailabilityDetail): string => {
    const start = dayjs(detail.start_datetime);
    const end = detail.end_datetime ? dayjs(detail.end_datetime) : null;

    if (!end) return start.format('h:mm A');

    return start.isSame(end, 'day') ?
            `${start.format('h:mm A')} - ${end.format('h:mm A')}`
        :   `${start.format('h:mm A')} - ${end.format('DD/MM/YY h:mm A')}`;
};
</script>
